// Sep 2026 (Randy) — Payments-audit history repair, in the UI.
//
// The Sep 2026 payments-audit fixes (F4/F5/F7) only corrected NEW rows. The
// repair for money that already moved lived in a script that needs a shell on
// the pod, which a deployed production app does not give you. Same code, behind
// a button: preview first, then apply. Mongo-only, no Stripe calls, idempotent.
import { useState } from "react";
import { Wrench, Spinner, CheckCircle, MagnifyingGlass } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const ROWS = [
  {
    key: "f4_silo_subscription_price_cents", count: "fixed",
    label: "Paid fan subscriptions with no price on file",
    why: "Zeroes the creator tax export and disables the renewal reminder.",
    note: (d) => (d.skipped_no_silo_price
      ? `${d.skipped_no_silo_price} left alone — their silo has no price set`
      : null),
  },
  {
    key: "f5_tip_payment_transactions", count: "created",
    label: "Tips missing from the payment ledger",
    why: "Four surfaces read these rows, including the tax export and the Fan Portal.",
    note: (d) => (d.already_present ? `${d.already_present} already present` : null),
  },
  {
    key: "f7_dfy_operator_payout_rows", count: "fixed",
    label: "Done-for-You operator payouts missing their payout fields",
    why: "The payout sweep skips them, so the operator is never paid automatically.",
    note: () => null,
  },
];

export default function PaymentsAuditBackfillPanel() {
  const [busy, setBusy] = useState(null);
  const [result, setResult] = useState(null);

  const run = async (apply) => {
    setBusy(apply ? "apply" : "dry");
    try {
      const r = await api.post("/admin/backfill/payments-audit",
        apply ? { apply: true, confirm: "APPLY" } : {});
      setResult(r.data);
      if (r.data.nothing_to_do) toast.success("Nothing to repair — the history is already correct.");
      else if (apply) toast.success(`Repaired ${r.data.total_rows} row${r.data.total_rows === 1 ? "" : "s"}.`);
      else toast.info(`${r.data.total_rows} row${r.data.total_rows === 1 ? "" : "s"} need repair. Nothing written yet.`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't run the repair.");
    } finally { setBusy(null); }
  };

  const applied = result?.mode === "APPLY";
  const canApply = result?.mode === "DRY_RUN" && !result.nothing_to_do;

  return (
    <section className="mt-8 bg-white border hairline rounded-lg overflow-hidden"
      data-testid="payments-audit-backfill">
      <div className="px-4 py-3 border-b hairline bg-[#FDFCF8] flex items-start gap-3">
        <Wrench size={15} weight="duotone" className="text-[#4A5D4E] shrink-0 mt-0.5" />
        <div className="min-w-0 flex-1">
          <div className="font-serif-display text-base text-[#1C1B1A]">History repair</div>
          <div className="text-[11px] text-[#6B6A68] leading-relaxed mt-0.5">
            The Sep 2026 payments fixes only corrected new records. This repairs the same three
            things for money that already moved. It reads and writes this database only — no Stripe
            calls, no money moves, and running it twice changes nothing the second time.
          </div>
        </div>
      </div>

      <div className="p-4 space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={() => run(false)}
            disabled={!!busy}
            data-testid="payments-audit-dry-run-btn"
            className="inline-flex items-center gap-1.5 text-xs px-3 py-2 rounded-md border hairline bg-white text-[#1C1B1A] hover:border-[#4A5D4E] disabled:opacity-50 transition-colors"
          >
            {busy === "dry"
              ? <Spinner size={13} className="animate-spin" />
              : <MagnifyingGlass size={13} weight="bold" />}
            Check what needs repair
          </button>
          <button
            onClick={() => run(true)}
            disabled={!!busy || !canApply}
            data-testid="payments-audit-apply-btn"
            title={canApply ? "" : "Run the check first."}
            className="inline-flex items-center gap-1.5 text-xs px-3 py-2 rounded-md bg-[#3A4A3E] text-[#FDFCF8] hover:bg-[#2F3D33] disabled:opacity-40 disabled:hover:bg-[#3A4A3E] transition-colors"
          >
            {busy === "apply" ? <Spinner size={13} className="animate-spin" /> : <Wrench size={13} weight="bold" />}
            Apply repair
          </button>
          {!result && (
            <span className="text-[10px] text-[#9C9A94] italic">
              The check writes nothing — it only counts.
            </span>
          )}
        </div>

        {result && result.nothing_to_do && (
          <div className="flex items-center gap-2 text-xs text-[#2F6B3A] bg-[#F1F7F1] border border-[#B7CBB9] rounded-md px-3 py-2.5"
            data-testid="payments-audit-nothing-to-do">
            <CheckCircle size={14} weight="fill" className="shrink-0" />
            Nothing to repair. Every record this checks is already correct.
          </div>
        )}

        {result && !result.nothing_to_do && (
          <div className="border hairline rounded-md overflow-hidden" data-testid="payments-audit-result">
            <div className={`px-3 py-2 text-[11px] font-medium border-b hairline ${applied
              ? "bg-[#F1F7F1] text-[#2F6B3A]" : "bg-[#FDF8EE] text-[#8A5A00]"}`}
              data-testid="payments-audit-total">
              {applied
                ? `Repaired ${result.total_rows} record${result.total_rows === 1 ? "" : "s"}.`
                : `${result.total_rows} record${result.total_rows === 1 ? "" : "s"} need repair. Nothing has been written yet.`}
            </div>
            {ROWS.map((row) => {
              const d = result[row.key] || {};
              const n = d[row.count] || 0;
              const note = row.note(d);
              return (
                <div key={row.key}
                  className="px-3 py-2.5 border-b hairline last:border-0 flex items-start gap-3"
                  data-testid={`payments-audit-row-${row.key}`}>
                  <span className={`text-sm tabular-nums font-medium shrink-0 w-8 text-right ${n > 0
                    ? (applied ? "text-[#2F6B3A]" : "text-[#8A5A00]") : "text-[#9C9A94]"}`}>
                    {n}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-[11px] text-[#1C1B1A]">{row.label}</span>
                    <span className="block text-[10px] text-[#6B6A68] leading-relaxed">
                      {n > 0 ? row.why : "None found."}
                      {note && <span className="text-[#9C9A94]"> · {note}</span>}
                    </span>
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}
